/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "./Log";
import { Sage } from "./Sage";
import { PendingTelemetryStore } from "./PendingTelemetryStore";
import { SageIdentityGate } from "./SageIdentityGate";
import { OperationalTransaction, type LogDetails } from "../types";

const TAG = "OperationalLogger";

const KEY_MULTIVIEW_INDEX = "multiviewIndex";
const KEY_TRANSACTION_ID = "transactionId";
const KEY_REASON = "reason";
const MULTIVIEW_SUFFIX = "MultiView";
const UNKNOWN_SESSION = "unknown";

const EVENT_WATCH_CONTENT_START = "watchContentStart";
const EVENT_WATCH_CONTENT_END = "watchContentEnd";
const EVENT_STREAMING_START = "streamingStart";
const EVENT_STREAMING_END = "streamingEnd";
const EVENT_CANCEL = "operations-cancel-transaction";
const EVENT_PLAYBACK_START = "playbackStart";
const EVENT_PLAYBACK_HEALTH = "playbackHealth";
const EVENT_PLAYBACK_END = "playbackEnd";
const EVENT_UI_ERROR = "uiError";
const EVENT_ERROR = "error";

/** Shared with `SignInLogger` — keep the name and fields byte-identical. */
const EVENT_API_REQUEST = "apiRequest";
const STATUS_FAILURE = "failure";
const UNKNOWN_ERROR_TYPE = "unknown";
const KEY_API_NAME = "api_name";
const KEY_SCREEN_ID = "screen_id";
const KEY_STATUS = "status";
const KEY_ERROR_TYPE = "error_type";
const KEY_ERROR_CODE = "error_code";
const KEY_SUB_ERROR_CODE = "sub_error_code";
const KEY_HTTP_STATUS = "http_status";
const KEY_LATENCY_MS = "latency_ms";

const KEY_ERROR_MESSAGE = "error_message";
const KEY_SCREEN = "screen";
const KEY_PLAYBACK_ID = "playback_id";

const TRANSACTION_EVENT_NAMES = {
  [OperationalTransaction.WatchContent]: {
    end: EVENT_WATCH_CONTENT_END,
    start: EVENT_WATCH_CONTENT_START
  },
  [OperationalTransaction.StreamingSession]: {
    end: EVENT_STREAMING_END,
    start: EVENT_STREAMING_START
  }
} as const;

/**
 * Screen-specific configuration passed to {@link OperationalLogger.create}.
 *
 * Define these as named constants in the screen's constants file and pass
 * them once at logger creation time rather than on every log call.
 */
export interface OperationalScreenConfig {
  screenId: string;
  apiEndpoint: string;
}

/**
 * Caller-supplied fields. A value may be `undefined`: the logger drops those
 * keys, so a call site can write a conditional field inline instead of
 * building the object in two steps.
 */
export type OperationalLogFields = Record<string, string | undefined>;

/** Everything known about one failed screen data-fetch. */
export interface OperationalApiFailureFields {
  /** Catalog code shown to the user, e.g. `A707`. Screens with an entry only. */
  errorCode?: string;
  /** Sub-error code, e.g. `G500`. Omitted when no layer recognised the error. */
  subErrorCode?: string;
  /** Milliseconds from request start to failure, when the caller timed it. */
  latencyMs?: number;
  /** Extra screen-specific fields. */
  extra?: OperationalLogFields;
}

/** Fields for a background, non-user-facing failure (`error` event). */
export interface OperationalErrorFields {
  /** Catalog code, e.g. `A707`. Sent empty when the screen has none. */
  errorCode?: string;
  errorMessage?: string;
}

/** Fields for a user-facing error dialog/overlay/failure state (`uiError` event). */
export interface OperationalUiErrorFields {
  /** Catalog code, e.g. `A707`. Sent empty when the screen has none. */
  errorCode?: string;
  errorMessage?: string;
  /** Screen where the error was displayed, e.g. `"Search"`, `"Guide"`. */
  screen?: string;
  /** Screen identifier, e.g. `"/screens/searchResults"`. */
  screenId?: string;
  /** Active playback session id — only when the error occurs during a player overlay search. */
  playbackId?: string;
}

/**
 * Operational logger backed by the {@link Sage} transport.
 *
 * Transactions are synthesized over operations events, matching the Android
 * OperationalLoggerImpl: well-known transaction names map to concrete
 * start/end events, a `transactionId` is generated (`<sessionId>_<counter>`)
 * and merged into details, and a `MultiView` suffix is appended when
 * `multiviewIndex` is present. Keep event names in sync with Android for
 * shared dashboards.
 *
 * Screen-scoped configuration is still available via
 * {@link OperationalLogger.create} for future stream-specific needs.
 */
export class OperationalLogger {
  private static counter = 0;

  /**
   * Stable screen identifier reserved for screen-scoped operational events.
   * Override via {@link OperationalLogger.create} with the actual screen endpoint.
   */
  protected static readonly SCREEN_ID: string = "";

  /**
   * API endpoint reserved for screen-scoped operational events.
   * Override via {@link OperationalLogger.create} with the data-fetch endpoint for this screen.
   */
  protected static readonly API_ENDPOINT: string = "";

  /**
   * Factory — creates a screen-specific logger bound to the supplied config.
   *
   * Each screen defines its own constants and passes them here once:
   *   const logger = OperationalLogger.create({
   *     screenId:    HOMESCREEN_CAROUSEL_SCREEN_ID,
   *     apiEndpoint: HOMESCREEN_CAROUSEL_API_ENDPOINT,
   *   });
   *
   * All static methods on the returned class carry the correct screen context
   * without any per-call configuration.
   */
  public static create(
    config: OperationalScreenConfig
  ): typeof OperationalLogger {
    return class extends OperationalLogger {
      protected static override readonly SCREEN_ID = config.screenId;
      protected static override readonly API_ENDPOINT = config.apiEndpoint;
    };
  }

  public static logEvent(name: string, details?: LogDetails): void {
    const eventName = OperationalLogger.addMultiViewSuffixIfNeeded(
      name,
      details
    );
    Log.d(TAG, `ops event: ${eventName}`);
    OperationalLogger.emit(eventName, details);
  }

  public static logPlaybackStart(details?: LogDetails): void {
    OperationalLogger.logEvent(EVENT_PLAYBACK_START, details);
  }

  public static logPlaybackHealth(details?: LogDetails): void {
    OperationalLogger.logEvent(EVENT_PLAYBACK_HEALTH, details);
  }

  public static logPlaybackEnd(details?: LogDetails): void {
    OperationalLogger.logEvent(EVENT_PLAYBACK_END, details);
  }

  public static transactionStart(name: string, details?: LogDetails): string {
    const transactionId = OperationalLogger.nextTransactionId();
    const enrichedDetails = OperationalLogger.withTransactionId(
      details,
      transactionId
    );
    const eventName = OperationalLogger.resolveTransactionEventName(name, true);

    const isWellKnown =
      name === OperationalTransaction.WatchContent ||
      name === OperationalTransaction.StreamingSession;
    const finalDetails: LogDetails = isWellKnown
      ? { ...enrichedDetails, [name]: transactionId }
      : enrichedDetails;

    const finalEventName = OperationalLogger.addMultiViewSuffixIfNeeded(
      eventName,
      finalDetails
    );
    Log.d(
      TAG,
      `ops transaction start: ${finalEventName} (id=${transactionId})`
    );
    OperationalLogger.emit(finalEventName, finalDetails);

    return transactionId;
  }

  public static transactionEnd(
    name: string,
    transactionId: string,
    details?: LogDetails
  ): void {
    const enrichedDetails = OperationalLogger.withTransactionId(
      details,
      transactionId
    );
    const eventName = OperationalLogger.resolveTransactionEventName(
      name,
      false
    );

    const finalEventName = OperationalLogger.addMultiViewSuffixIfNeeded(
      eventName,
      enrichedDetails
    );
    Log.d(TAG, `ops transaction end: ${finalEventName} (id=${transactionId})`);
    OperationalLogger.emit(finalEventName, enrichedDetails);
  }

  public static transactionCancel(
    transactionId: string,
    reason: string,
    details?: LogDetails
  ): void {
    const enrichedDetails = OperationalLogger.withTransactionId(
      details,
      transactionId
    );
    if (reason.length > 0) {
      enrichedDetails[KEY_REASON] = reason;
    }

    Log.d(
      TAG,
      `ops transaction cancel: ${EVENT_CANCEL} (id=${transactionId}, reason=${reason})`
    );
    OperationalLogger.emit(EVENT_CANCEL, enrichedDetails);
  }

  // ────────────────────────── Screen-scoped Events ─────────────────────────
  //
  // These use `this.SCREEN_ID` / `this.API_ENDPOINT`, resolved via TypeScript
  // static `this` polymorphism, so a logger built with
  // {@link OperationalLogger.create} carries its screen without per-call
  // configuration — the same shape `AnalyticsLogger` uses.

  /**
   * apiRequest — service health for one screen data-fetch that failed.
   *
   * Same event and field names `SignInLogger` emits, so screen failures land
   * in the existing service-health dashboard rather than a parallel one.
   *
   * `errorCode` / `subErrorCode` are the catalog code the user is looking at
   * (`A707`, `A707-G500`), so a support call can be matched to a row. Screens
   * without a catalog entry pass neither and report the error alone.
   *
   * Zero-PII: the error is read for its *type* and HTTP status only, never for
   * its message — that can echo back what the caller sent.
   *
   * @param error - The raw error, for type and status
   * @param fields - Catalog codes, latency, and any extra fields
   */
  public static logApiFailure(
    error: unknown,
    fields: OperationalApiFailureFields = {}
  ): void {
    const details: LogDetails = {
      [KEY_API_NAME]: this.API_ENDPOINT,
      [KEY_SCREEN_ID]: this.SCREEN_ID,
      [KEY_STATUS]: STATUS_FAILURE,
      [KEY_ERROR_TYPE]: OperationalLogger.describeErrorType(error)
    };

    OperationalLogger.assign(details, {
      [KEY_ERROR_CODE]: fields.errorCode,
      [KEY_SUB_ERROR_CODE]: fields.subErrorCode,
      [KEY_HTTP_STATUS]: OperationalLogger.extractHttpStatus(error),
      [KEY_LATENCY_MS]:
        fields.latencyMs === undefined ? undefined : String(fields.latencyMs)
    });
    OperationalLogger.assign(details, fields.extra);

    OperationalLogger.logEvent(EVENT_API_REQUEST, details);
  }

  /**
   * error — a background/non-user-facing failure that was handled silently
   * (retry, fallback, or suppressed, or a silently-retried timeout, a parse
   * failure, a history-sync error). Fire once per distinct occurrence.
   * `error_code` is sent empty when the caller has no catalog entry.
   */
  public static logError(fields: OperationalErrorFields = {}): void {
    const details: LogDetails = {
      [KEY_ERROR_CODE]: fields.errorCode !== undefined ? fields.errorCode : ""
    };
    OperationalLogger.assign(details, {
      [KEY_ERROR_MESSAGE]: fields.errorMessage
    });
    OperationalLogger.logEvent(EVENT_ERROR, details);
  }

  /**
   * A stable, PII-safe human-readable error message for telemetry.
   */
  public static describeErrorMessage(error: unknown): string {
    if (error instanceof Error && error.message.length > 0) {
      return error.message;
    }
    if (typeof error === "string" && error.length > 0) {
      return error;
    }
    return OperationalLogger.describeErrorType(error);
  }

  /**
   * uiError — a user-facing error dialog, overlay, or failure state was
   * displayed. Fire once per distinct occurrence. `error_code` is sent empty
   * when the caller has no catalog entry (e.g. no Search error-code catalog
   * exists yet).
   */
  public static logUiError(fields: OperationalUiErrorFields = {}): void {
    const details: LogDetails = {
      [KEY_ERROR_CODE]: fields.errorCode !== undefined ? fields.errorCode : "",
      [KEY_SCREEN]: fields.screen !== undefined ? fields.screen : "",
      [KEY_SCREEN_ID]: fields.screenId !== undefined ? fields.screenId : ""
    };
    OperationalLogger.assign(details, {
      [KEY_ERROR_MESSAGE]: fields.errorMessage,
      [KEY_PLAYBACK_ID]: fields.playbackId
    });
    OperationalLogger.logEvent(EVENT_UI_ERROR, details);
  }

  /**
   * A stable, PII-free label for an error: the domain error's own type, then
   * its transport code, then its constructor name.
   */
  public static describeErrorType(error: unknown): string {
    if (error === null || error === undefined) {
      return UNKNOWN_ERROR_TYPE;
    }
    if (typeof error !== "object") {
      return typeof error;
    }

    const candidate = error as {
      getType?: () => string;
      code?: unknown;
      name?: unknown;
    };

    if (typeof candidate.getType === "function") {
      const type: unknown = candidate.getType();
      if (typeof type === "string" && type.length > 0) {
        return type;
      }
    }
    if (typeof candidate.code === "string" && candidate.code.length > 0) {
      return candidate.code;
    }
    if (typeof candidate.name === "string" && candidate.name.length > 0) {
      return candidate.name;
    }
    return UNKNOWN_ERROR_TYPE;
  }

  /** HTTP status from an error that carries a response, as a string. */
  private static extractHttpStatus(error: unknown): string | undefined {
    if (error === null || error === undefined || typeof error !== "object") {
      return undefined;
    }
    const response = (error as { response?: { status?: unknown } }).response;
    if (response === undefined || response === null) {
      return undefined;
    }
    return typeof response.status === "number"
      ? String(response.status)
      : undefined;
  }

  /** Copies defined values only — undefined keys never reach the wire. */
  private static assign(
    target: LogDetails,
    fields?: OperationalLogFields
  ): void {
    if (fields === undefined) {
      return;
    }
    Object.keys(fields).forEach((key: string): void => {
      const value: string | undefined = fields[key];
      if (value !== undefined) {
        target[key] = value;
      }
    });
  }

  /** Test-only: resets the transaction counter for isolation between tests. */
  public static resetForTest(): void {
    OperationalLogger.counter = 0;
  }

  /**
   * Sends to Sage once identity exists, otherwise queues for replay.
   *
   * The queue preserves order, so a transaction whose start was queued and
   * whose end was sent live still arrives start-then-end: the flush runs
   * before any later live call, on the same identity hand-off.
   */
  private static emit(name: string, details?: LogDetails): void {
    if (SageIdentityGate.isReady()) {
      Sage.logOperations(name, details);
      return;
    }
    // Fire-and-forget — never block the caller
    PendingTelemetryStore.enqueueOperational(name, details).catch(() => {
      // enqueue() already logs its own error; nothing else to do
    });
  }

  private static withTransactionId(
    details: LogDetails | undefined,
    transactionId: string
  ): LogDetails {
    return {
      ...(details || {}),
      [KEY_TRANSACTION_ID]: transactionId
    };
  }

  private static resolveTransactionEventName(
    transactionName: string,
    isStart: boolean
  ): string {
    const eventNames =
      TRANSACTION_EVENT_NAMES[
        transactionName as keyof typeof TRANSACTION_EVENT_NAMES
      ];
    if (eventNames === undefined) {
      return transactionName;
    }
    return isStart ? eventNames.start : eventNames.end;
  }

  private static nextTransactionId(): string {
    const _sessionId = Sage.getSessionId();
    const sessionId =
      _sessionId === undefined || _sessionId === null
        ? UNKNOWN_SESSION
        : _sessionId;
    OperationalLogger.counter += 1;
    return `${sessionId}_${OperationalLogger.counter}`;
  }

  private static addMultiViewSuffixIfNeeded(
    eventName: string,
    details?: LogDetails
  ): string {
    if (details && KEY_MULTIVIEW_INDEX in details) {
      return `${eventName}${MULTIVIEW_SUFFIX}`;
    }
    return eventName;
  }
}
